import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { formatDateISO, parseDateISO, todayISO } from '../../utils/dateHelpers';
import { Calendar } from '../../components/Calendar';
import type { DayData } from '../../components/Calendar/Calendar.types';
import {
  ApplyButton,
  Footer,
  LoadingOverlay,
  LoadMoreButton,
  MonthsContainer,
  Panel,
  ResetButton,
  StackedWeekdayLabel,
  StackedWeekdays,
} from './DatePicker.styles';
import type { DatePickerProps, SelectionPhase } from './DatePicker.types';

function getDefaultWeekdays(): string[] {
  const wd = moment.localeData().weekdaysMin();
  return [...wd.slice(1), wd[0]].map((d) => d.charAt(0).toUpperCase());
}

function getMaxCheckoutDate(
  checkInDate: string,
  availability?: Map<string, DayData>
): string | null {
  if (!availability) return null;

  const start = parseDateISO(checkInDate);
  const maxLookahead = 365;

  for (let i = 1; i <= maxLookahead; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const ds = formatDateISO(d);
    const dayData = availability.get(ds);

    if (dayData && (dayData.type === 'unavailable' || dayData.type === 'hotel_closed')) {
      const prev = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i - 1);
      return formatDateISO(prev);
    }
  }

  return null;
}

export function DatePicker({
  checkIn: externalCheckIn,
  checkOut: externalCheckOut,
  singleDateMode = false,
  numberOfMonths = 2,
  availability,
  isLoading,
  onRangeSelect,
  onMonthChange,
  onReset,
  onApply,
  columnView = false,
  showFooter = false,
  stacked: stackedProp = false,
  labels: l,
}: DatePickerProps) {
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 600px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const stacked = stackedProp || isMobileViewport;
  const initialMonthCount = stacked ? Math.max(numberOfMonths, 2) : numberOfMonths;
  const [monthsToShow, setMonthsToShow] = useState(initialMonthCount);
  const [phase, setPhase] = useState<SelectionPhase>('check-in');
  const [internalCheckIn, setInternalCheckIn] = useState<string | null>(externalCheckIn ?? null);
  const [internalCheckOut, setInternalCheckOut] = useState<string | null>(externalCheckOut ?? null);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  const [visibleMonth, setVisibleMonth] = useState<Date>(() => {
    if (externalCheckIn) {
      const d = parseDateISO(externalCheckIn);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  useEffect(() => {
    setInternalCheckIn(externalCheckIn ?? null);
    setInternalCheckOut(externalCheckOut ?? null);
  }, [externalCheckIn, externalCheckOut]);

  const maxCheckout =
    phase === 'check-out' && internalCheckIn
      ? getMaxCheckoutDate(internalCheckIn, availability)
      : null;

  const effAvailability = useMemo((): Map<string, DayData> | undefined => {
    if (!availability) return undefined;
    if (phase !== 'check-out' || !internalCheckIn || !maxCheckout) return availability;

    const map = new Map(availability);
    for (const [dateStr, data] of map) {
      if (dateStr > maxCheckout && data.type === 'available') {
        map.set(dateStr, { ...data, type: 'unavailable' });
      }
    }
    return map;
  }, [availability, phase, internalCheckIn, maxCheckout]);

  const goToPrevMonth = () => {
    const prev = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1);
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    if (prev >= currentMonth) {
      setVisibleMonth(prev);
      onMonthChange?.(prev);
    }
  };

  const goToNextMonth = () => {
    const next = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);
    setVisibleMonth(next);
    onMonthChange?.(next);
  };

  const now = new Date();
  const canGoPrev =
    visibleMonth.getFullYear() > now.getFullYear() ||
    (visibleMonth.getFullYear() === now.getFullYear() && visibleMonth.getMonth() > now.getMonth());

  const handleDayClick = (dateStr: string) => {
    const today = todayISO();
    if (dateStr < today) return;

    if (singleDateMode) {
      setInternalCheckIn(dateStr);
      setInternalCheckOut(null);
      onRangeSelect(dateStr, dateStr);
      return;
    }

    if (phase === 'check-in') {
      setInternalCheckIn(dateStr);
      setInternalCheckOut(null);
      setPhase('check-out');
    } else {
      if (dateStr <= internalCheckIn!) {
        setInternalCheckIn(dateStr);
        setInternalCheckOut(null);
        setPhase('check-out');
      } else {
        setInternalCheckOut(dateStr);
        setPhase('check-in');
        onRangeSelect(internalCheckIn!, dateStr);
      }
    }
  };

  const weekdaysForHeader = l?.weekdays ?? getDefaultWeekdays();
  const calendarLabels =
    l?.weekdays || l?.months ? { weekdays: l.weekdays, months: l.months } : undefined;

  const isDouble = numberOfMonths > 1;
  const monthList = stacked
    ? Array.from({ length: monthsToShow }, (_, i) =>
        new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + i, 1)
      )
    : [
        visibleMonth,
        ...(isDouble
          ? [new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1)]
          : []),
      ];

  const handleLoadMore = () => {
    const next = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + monthsToShow, 1);
    onMonthChange?.(next);
    setMonthsToShow((c) => c + 1);
  };

  return (
    <Panel
      $columnView={columnView}
      $singleMonth={numberOfMonths === 1}
      $stacked={stacked}
    >
      {stacked && (
        <StackedWeekdays>
          {weekdaysForHeader.map((wd, i) => (
            <StackedWeekdayLabel key={i}>{wd}</StackedWeekdayLabel>
          ))}
        </StackedWeekdays>
      )}
      <MonthsContainer
        $columnView={columnView}
        $singleMonth={numberOfMonths === 1}
        $stacked={stacked}
      >
        {monthList.map((m, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === monthList.length - 1;
          return (
            <Calendar
              key={`${m.getFullYear()}-${m.getMonth()}`}
              year={m.getFullYear()}
              month={m.getMonth()}
              availability={effAvailability}
              checkIn={internalCheckIn}
              checkOut={internalCheckOut}
              isSelectingCheckOut={phase === 'check-out'}
              hoveredDate={hoveredDate}
              onDayClick={handleDayClick}
              onDayHover={setHoveredDate}
              onPrevMonth={!stacked && isFirst ? goToPrevMonth : undefined}
              prevMonthDisabled={!canGoPrev}
              onNextMonth={!stacked && isLast ? goToNextMonth : undefined}
              hideWeekdays={stacked}
              labels={calendarLabels}
            />
          );
        })}
        {stacked && (
          <LoadMoreButton type="button" onClick={handleLoadMore}>
            {l?.loadMore ?? 'Load more'}
          </LoadMoreButton>
        )}
      </MonthsContainer>

      {showFooter && (
        <Footer>
          <ResetButton
            type="button"
            onClick={onReset}
            disabled={!onReset || (!internalCheckIn && !internalCheckOut)}
          >
            {l?.reset ?? 'Reset'}
          </ResetButton>
          <ApplyButton
            type="button"
            onClick={onApply}
            disabled={!onApply || !internalCheckIn || (!singleDateMode && !internalCheckOut)}
          >
            {l?.apply ?? 'Apply'}
          </ApplyButton>
        </Footer>
      )}

      {isLoading && (
        <LoadingOverlay>{l?.loadingAvailability ?? 'Loading availability...'}</LoadingOverlay>
      )}
    </Panel>
  );
}
